import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Privacy Page — Revela
 * Riscritta 2026-05-28: estetica argento/oro coerente con la landing,
 * conformità GDPR completa (titolare, basi giuridiche, retention, diritti,
 * trasferimenti, AI Act, k-anonimato).
 */
const Section = ({ eyebrow, title, children, testId }) => (
  <section className="border-b border-slate-200" data-testid={testId}>
    <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-14">
      {eyebrow && (
        <p className="text-xs sm:text-sm text-gold font-medium tracking-[0.18em] uppercase mb-3">
          {eyebrow}
        </p>
      )}
      {title && (
        <h2 className="font-display text-2xl sm:text-3xl text-slate-800 font-light leading-tight mb-5">
          {title}
        </h2>
      )}
      <div className="space-y-4 text-base sm:text-[17px] text-slate-700 leading-relaxed font-light">
        {children}
      </div>
    </div>
  </section>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3">
    <span className="w-1 h-1 rounded-full bg-amber-700/50 mt-2.5 shrink-0" />
    <span>{children}</span>
  </li>
);

const PrivacyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-slate-800" data-testid="privacy-page-revela">
      {/* Top bar */}
      <div className="border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-5 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 font-light transition-colors"
            data-testid="privacy-back-btn"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Torna indietro
          </button>
          <span className="text-[11px] text-slate-400 tracking-[0.2em] uppercase font-light">Revela</span>
        </div>
      </div>

      {/* Hero */}
      <section className="border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 pt-16 sm:pt-20 pb-12">
          <p className="text-xs sm:text-sm text-gold font-medium tracking-[0.18em] uppercase mb-4">
            Privacy & Trattamento dei dati
          </p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-gold font-light leading-tight mb-6">
            I tuoi dati, con cura.
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 leading-relaxed font-light max-w-2xl">
            Revela è progettata, fin dall'origine, secondo i principi del <strong className="font-medium">GDPR</strong> e della <em>privacy by design</em>. Questa pagina spiega in modo trasparente quali dati raccogliamo, perché, come li proteggiamo e quali diritti puoi esercitare in qualsiasi momento.
          </p>
          <p className="text-sm text-slate-400 italic font-light mt-6">
            Ultimo aggiornamento: maggio 2026
          </p>
        </div>
      </section>

      {/* Punto fondamentale */}
      <section className="border-b border-slate-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-14">
          <div className="bg-white rounded-lg p-6 sm:p-8 box-gold box-gold-accent" data-testid="privacy-key-point">
            <p className="text-xs sm:text-sm text-gold font-medium tracking-[0.18em] uppercase mb-3">
              Il punto fondamentale
            </p>
            <p className="font-display text-2xl sm:text-3xl text-slate-800 font-light leading-snug mb-4">
              I tuoi check-in sono tuoi, e solo tuoi.
            </p>
            <p className="text-base sm:text-[17px] text-slate-700 leading-relaxed font-light">
              Non leggiamo i contenuti che inserisci. Non li condividiamo con terzi. Possono essere usati esclusivamente in forma <strong className="font-medium">completamente anonima e aggregata</strong> (standard <strong className="font-medium">k-anonimato, k ≥ 5</strong>) per migliorare l'app — mai per identificarti.
            </p>
          </div>
        </div>
      </section>

      {/* Titolare */}
      <Section eyebrow="Titolare del trattamento" title="Chi è responsabile dei tuoi dati" testId="privacy-controller">
        <p>
          Titolare del trattamento dei dati personali è <strong className="font-medium">Dott.ssa Laura Piccinini</strong>, psicologa e psicoterapeuta, in qualità di titolare del progetto Revela.
        </p>
        <ul className="space-y-2 pt-2">
          <Bullet>Sito professionale: <a href="https://www.laurapiccinini.it" target="_blank" rel="noopener noreferrer" className="text-slate-700 underline decoration-amber-700/40 underline-offset-2 hover:text-slate-900">www.laurapiccinini.it</a></Bullet>
          <Bullet>Per esercitare i tuoi diritti o per richieste relative alla privacy puoi contattare il Titolare tramite i recapiti indicati sul sito.</Bullet>
        </ul>
      </Section>

      {/* Categorie di dati */}
      <Section eyebrow="Categorie di dati" title="Quali dati trattiamo" testId="privacy-data-categories">
        <p>Trattiamo solo i dati strettamente necessari a farti funzionare Revela, organizzati in tre categorie:</p>
        <div className="grid grid-cols-1 gap-4 pt-2">
          <div className="bg-white rounded-lg p-5 box-gold">
            <p className="text-[13px] text-slate-800 font-semibold tracking-[0.08em] uppercase mb-2">Dati di identificazione</p>
            <p className="text-base text-slate-600 font-light leading-relaxed">Email e nome forniti tramite Google OAuth al momento dell'accesso. Non memorizziamo password.</p>
          </div>
          <div className="bg-white rounded-lg p-5 box-gold">
            <p className="text-[13px] text-slate-800 font-semibold tracking-[0.08em] uppercase mb-2">Dati esperienziali (check-in EMA)</p>
            <p className="text-base text-slate-600 font-light leading-relaxed">I dati che inserisci volontariamente nei check-in: contesto (dove, con chi, attività) e le sei dimensioni soggettive di vissuto. Possono riferirsi al tuo stato d'animo e sono pertanto trattati come <strong className="font-medium">categorie particolari di dati</strong> ai sensi dell'art. 9 GDPR.</p>
          </div>
          <div className="bg-white rounded-lg p-5 box-gold">
            <p className="text-[13px] text-slate-800 font-semibold tracking-[0.08em] uppercase mb-2">Dati tecnici minimi</p>
            <p className="text-base text-slate-600 font-light leading-relaxed">Log di accesso, identificativo di sessione, informazioni essenziali al funzionamento sicuro del servizio. Nessun tracciamento pubblicitario, nessuna profilazione di terzi.</p>
          </div>
        </div>
      </Section>

      {/* Finalità e basi giuridiche */}
      <Section eyebrow="Finalità & basi giuridiche" title="Perché trattiamo i tuoi dati" testId="privacy-purposes">
        <ul className="space-y-3">
          <Bullet>
            <strong className="font-medium">Erogazione del servizio</strong> (autenticazione, salvataggio dei check-in, calcolo dei tuoi indicatori personali) — base giuridica: <em>esecuzione di un contratto</em> di cui sei parte (art. 6.1.b GDPR).
          </Bullet>
          <Bullet>
            <strong className="font-medium">Trattamento di dati riferibili allo stato d'animo</strong> (categorie particolari, art. 9 GDPR) — base giuridica: <em>tuo consenso esplicito</em> prestato al primo accesso, sempre revocabile.
          </Bullet>
          <Bullet>
            <strong className="font-medium">Statistiche aggregate e anonime</strong> per migliorare l'app — base giuridica: <em>legittimo interesse</em> del Titolare, applicato solo dopo anonimizzazione con k-anonimato (k ≥ 5).
          </Bullet>
          <Bullet>
            <strong className="font-medium">Sicurezza e prevenzione frodi</strong> — base giuridica: <em>legittimo interesse</em> + <em>obbligo legale</em>.
          </Bullet>
        </ul>
      </Section>

      {/* Sicurezza */}
      <Section eyebrow="Sicurezza" title="Come proteggiamo i tuoi dati" testId="privacy-security">
        <ul className="space-y-3">
          <Bullet><strong className="font-medium">Crittografia in transito (HTTPS / TLS).</strong> Tutti i dati viaggiano cifrati tra il tuo dispositivo e i nostri server.</Bullet>
          <Bullet><strong className="font-medium">Crittografia at-rest (AES-256).</strong> I contenuti sensibili sono cifrati nel database: anche un accesso non autorizzato vedrebbe solo dati illeggibili.</Bullet>
          <Bullet><strong className="font-medium">Isolamento per utente.</strong> Ogni account può accedere esclusivamente ai propri dati. Non esiste alcuna interfaccia che permetta a noi di leggere i contenuti dei tuoi check-in.</Bullet>
          <Bullet><strong className="font-medium">Autenticazione delegata a Google OAuth.</strong> Non gestiamo né memorizziamo le tue password.</Bullet>
          <Bullet><strong className="font-medium">k-anonimato (k ≥ 5)</strong> applicato sistematicamente prima di qualsiasi aggregazione: nessun dato pubblicato in forma che permetta la re-identificazione.</Bullet>
        </ul>
      </Section>

      {/* Servizi terzi */}
      <Section eyebrow="Responsabili del trattamento" title="Servizi terzi che ci aiutano a farti funzionare Revela" testId="privacy-processors">
        <p>I seguenti fornitori operano in qualità di <em>Responsabili del trattamento</em> ai sensi dell'art. 28 GDPR, vincolati da accordi (DPA) che garantiscono il livello di protezione richiesto:</p>
        <ul className="space-y-3 pt-2">
          <Bullet><strong className="font-medium">Google LLC</strong> — autenticazione OAuth, finalità: identificare l'utente al login.</Bullet>
          <Bullet><strong className="font-medium">MongoDB Atlas</strong> — hosting cifrato del database delle applicazioni.</Bullet>
          <Bullet><strong className="font-medium">OpenAI / Emergent Integrations</strong> — generazione di riassunti settimanali in linguaggio naturale. All'AI viene inviato esclusivamente il testo pseudonimizzato necessario; mai email, nome o identificativi.</Bullet>
          <Bullet><strong className="font-medium">Apple APNs</strong> — invio di notifiche push (solo se le abiliti).</Bullet>
          <Bullet><strong className="font-medium">Stripe</strong> — gestione di eventuali pagamenti futuri (al momento Revela è gratuita in fase beta).</Bullet>
        </ul>
      </Section>

      {/* Trasferimenti extra-UE */}
      <Section eyebrow="Trasferimenti extra-UE" title="Quando i dati escono dall'Europa" testId="privacy-transfers">
        <p>
          Alcuni dei nostri fornitori (Google, OpenAI, Stripe) hanno sede negli Stati Uniti. Eventuali trasferimenti extra-UE avvengono nel rispetto degli artt. 44 e seguenti GDPR, tramite <strong className="font-medium">Clausole Contrattuali Standard</strong> approvate dalla Commissione Europea e, ove applicabile, in adesione al <strong className="font-medium">Data Privacy Framework UE-USA</strong>. Adottiamo inoltre misure aggiuntive (pseudonimizzazione, cifratura) per minimizzare i rischi.
        </p>
      </Section>

      {/* Conservazione */}
      <Section eyebrow="Conservazione" title="Per quanto tempo conserviamo i dati" testId="privacy-retention">
        <ul className="space-y-3">
          <Bullet><strong className="font-medium">Dati di account</strong>: finché mantieni attivo l'account, più <strong className="font-medium">30 giorni</strong> dopo la cancellazione (per consentire ripensamenti).</Bullet>
          <Bullet><strong className="font-medium">Check-in EMA e contenuti personali</strong>: conservati finché mantieni attivo l'account; puoi esportarli e cancellarli in qualsiasi momento.</Bullet>
          <Bullet><strong className="font-medium">Backup</strong>: rotazione automatica a <strong className="font-medium">90 giorni</strong>.</Bullet>
          <Bullet><strong className="font-medium">Dati aggregati anonimi</strong> (k ≥ 5): conservati senza limite di tempo, perché non più riconducibili a persona identificata o identificabile.</Bullet>
        </ul>
      </Section>

      {/* Uso AI */}
      <Section eyebrow="Intelligenza artificiale" title="Come — e quanto poco — usiamo l'IA" testId="privacy-ai">
        <p>L'app utilizza modelli di IA generativa esclusivamente per produrre <strong className="font-medium">riassunti settimanali</strong> in linguaggio naturale a partire dai tuoi indicatori. L'uso è limitato e trasparente:</p>
        <ul className="space-y-3 pt-2">
          <Bullet><strong className="font-medium">Nessun dato identificativo</strong> viene mai inviato all'IA (mai email, nome, identificativo).</Bullet>
          <Bullet><strong className="font-medium">Nessuna decisione automatizzata</strong> che produca effetti giuridici o significativamente incida sulla persona (art. 22 GDPR).</Bullet>
          <Bullet><strong className="font-medium">Controllo umano (human-in-the-loop)</strong>: l'IA propone, tu valuti.</Bullet>
          <Bullet><strong className="font-medium">Nessuna profilazione</strong> a fini commerciali o pubblicitari.</Bullet>
          <Bullet>Allineamento ai principi del <strong className="font-medium">AI Act</strong> europeo e alle <em>Linee Guida CNOP</em> per l'uso dell'IA nella professione psicologica.</Bullet>
        </ul>
      </Section>

      {/* Diritti */}
      <Section eyebrow="I tuoi diritti" title="Cosa puoi chiederci in qualsiasi momento" testId="privacy-rights">
        <p>In qualità di interessato puoi esercitare, in qualsiasi momento e a titolo gratuito, i diritti previsti dagli artt. 15-22 GDPR:</p>
        <ul className="space-y-3 pt-2">
          <Bullet><strong className="font-medium">Accesso</strong> ai tuoi dati (art. 15).</Bullet>
          <Bullet><strong className="font-medium">Rettifica</strong> di dati inesatti o incompleti (art. 16).</Bullet>
          <Bullet><strong className="font-medium">Cancellazione</strong> ("diritto all'oblio") (art. 17).</Bullet>
          <Bullet><strong className="font-medium">Limitazione</strong> del trattamento (art. 18).</Bullet>
          <Bullet><strong className="font-medium">Portabilità</strong>: ricevere i tuoi dati in formato strutturato e leggibile (art. 20).</Bullet>
          <Bullet><strong className="font-medium">Opposizione</strong> al trattamento (art. 21).</Bullet>
          <Bullet><strong className="font-medium">Revoca del consenso</strong> in qualsiasi momento, senza pregiudicare la liceità dei trattamenti pregressi.</Bullet>
          <Bullet><strong className="font-medium">Reclamo al Garante per la protezione dei dati personali</strong> (<a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer" className="underline decoration-amber-700/40 underline-offset-2 hover:text-slate-900">www.garanteprivacy.it</a>) qualora ritenessi violati i tuoi diritti.</Bullet>
        </ul>
        <p className="pt-2 italic text-slate-600">
          La maggior parte di queste azioni (esportare, cancellare i dati, revocare il consenso) è eseguibile in autonomia direttamente dalle Impostazioni dell'app, senza dover chiedere nulla a noi.
        </p>
      </Section>

      {/* Cosa non facciamo */}
      <Section eyebrow="Quello che non facciamo, mai" title="Le nostre promesse non negoziabili" testId="privacy-never">
        <ul className="space-y-3">
          <Bullet>Non vendiamo i tuoi dati a nessuno.</Bullet>
          <Bullet>Non li condividiamo per finalità pubblicitarie o di marketing.</Bullet>
          <Bullet>Non utilizziamo cookie di tracciamento o di profilazione.</Bullet>
          <Bullet>Non leggiamo i contenuti dei tuoi check-in: l'architettura non lo consente.</Bullet>
          <Bullet>Non prendiamo decisioni automatizzate che producano effetti giuridici sulla tua persona.</Bullet>
        </ul>
      </Section>

      {/* Cookie */}
      <Section eyebrow="Cookie" title="Solo cookie tecnici essenziali" testId="privacy-cookies">
        <p>Utilizziamo unicamente cookie tecnici necessari al funzionamento dell'app (es. mantenere attiva la sessione di accesso). Non installiamo cookie di analytics di terze parti, né cookie pubblicitari o di profilazione.</p>
      </Section>

      {/* Disclaimer */}
      <Section eyebrow="Natura del servizio" title="Revela non è uno strumento clinico" testId="privacy-disclaimer">
        <p>
          Revela non fornisce valutazioni cliniche o psicologiche e non sostituisce in alcun modo una consulenza professionale. È uno strumento che ti aiuta a osservare pattern soggettivi nelle tue esperienze quotidiane. I suggerimenti che riceverai sono spunti di riflessione, da valutare sempre con il tuo personale discernimento. In presenza di sofferenza psicologica significativa, ti invitiamo a rivolgerti a un professionista qualificato.
        </p>
      </Section>

      {/* Contatti */}
      <section className="bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 text-center" data-testid="privacy-contacts">
          <p className="text-xs sm:text-sm text-gold font-medium tracking-[0.18em] uppercase mb-4">Contatti privacy</p>
          <p className="font-display text-3xl sm:text-4xl text-gold font-light leading-tight mb-5">
            Hai domande? Scrivici.
          </p>
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed font-light max-w-2xl mx-auto mb-6">
            Per esercitare i tuoi diritti o per qualsiasi richiesta relativa al trattamento dei tuoi dati, puoi contattare il Titolare ai recapiti indicati sui suoi siti professionali.
          </p>
          <p className="text-base text-slate-700 font-light">
            <strong className="font-medium">Dott.ssa Laura Piccinini</strong><br />
            <a href="https://www.laurapiccinini.it" target="_blank" rel="noopener noreferrer" className="underline decoration-amber-700/40 underline-offset-2 hover:text-slate-900">www.laurapiccinini.it</a>
            <span className="text-slate-400 mx-2">·</span>
            <a href="https://www.psicoapplicata.org" target="_blank" rel="noopener noreferrer" className="underline decoration-amber-700/40 underline-offset-2 hover:text-slate-900">www.psicoapplicata.org</a>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-6 text-center">
          <p className="text-[11px] text-slate-400 font-light tracking-wide">
            © {new Date().getFullYear()} Revela · Laura Piccinini ·{" "}
            <a href="/" className="hover:text-slate-600 underline decoration-slate-200 underline-offset-2">Home</a>
            {" · "}
            <a href="/termini" className="hover:text-slate-600 underline decoration-slate-200 underline-offset-2">Termini</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
